/**
 * Bulk edit configuration for Units.
 *
 * The unit bulk UI is a wizard (BulkEditWizardModal) with the same steps as
 * the unit wizard that hold bulk-editable data: info, additional info and
 * emergency contacts.
 *
 * - Step 1 (info) renders these fields through the generic BulkEditFieldsForm.
 * - Step 2 (additional info) edits `additional_info`: a key/value list.
 * - Step 3 (emergency contacts) edits `emergency_contact`: a list of
 *   { section, name, phone_number, description }.
 *
 * Both lists REPLACE the whole list on every selected unit (the backend
 * swaps them to avoid duplicates); the wizard warns the user about it. They
 * are not defined below because they are not plain inputs: the wizard owns
 * their UI and these keys only need to be seeded in formData.
 *
 * Each field defines:
 * - key:          Field key in the payload (e.g. 'id_category')
 * - label:        i18n key for the label
 * - type:         Field type ('select' | 'text' | 'number' | 'multiselect')
 * - optionsKey:   Options prop name in catalogOptions
 * - reduce:       Function that extracts the value from the v-select
 * - optionLabel:  Label property in the options (default: 'label')
 * - clearable:    Whether the field allows clearing the selection
 * - cols:         Bootstrap grid columns
 * - dependsOn:    Key of the field that must be filled first. The field stays
 *                 disabled until then and its placeholder names that field.
 */
export const BULK_EDIT_FIELDS = [
  {
    key: "id_category",
    label: "unit.category",
    type: "select",
    optionsKey: "categoryOptions",
    reduce: (val) => val.value,
    clearable: true,
    cols: 6,
  },
  {
    key: "id_tenant",
    label: "unit.tenant",
    type: "select",
    optionsKey: "tenantOptions",
    reduce: (val) => val.value,
    clearable: true,
    cols: 6,
  },
  {
    key: "id_center",
    label: "unit.center",
    type: "select",
    optionsKey: "centerOptions",
    reduce: (val) => val.value,
    clearable: true,
    cols: 6,
  },
  {
    key: "id_sector",
    label: "unit.sector",
    type: "select",
    optionsKey: "sectorOptions",
    reduce: (val) => val.value,
    clearable: true,
    cols: 6,
  },
  {
    key: "id_country",
    label: "country.title",
    type: "select",
    optionsKey: "countryOptions",
    reduce: (val) => val.value,
    clearable: true,
    cols: 6,
  },
  {
    key: "id_region",
    label: "region.title",
    type: "select",
    optionsKey: "regionOptions",
    reduce: (val) => val.value,
    clearable: true,
    cols: 6,
    dependsOn: "id_country",
  },
  {
    key: "id_municipality",
    label: "municipality.title",
    type: "select",
    optionsKey: "municipalityOptions",
    reduce: (val) => val.value,
    clearable: true,
    cols: 6,
    dependsOn: "id_region",
  },
  {
    key: "time_zone",
    label: "unit.timeZone",
    type: "select",
    optionsKey: "timezoneOptions",
    optionLabel: "name",
    reduce: (val) => val.name,
    clearable: true,
    cols: 6,
  },
  {
    key: "is_saturday",
    label: "unit.is_saturday",
    type: "select",
    clearable: true,
    cols: 6,
    reduce: (val) => val.value,
    optionsKey: "isSaturdayOptions",
  },
];

/**
 * Extra formData keys seeded by the wizard steps that own their own UI.
 * Each holds an array; null/[] means untouched.
 */
export const BULK_EDIT_ADDITIONAL_INFO_KEY = "additional_info";
export const BULK_EDIT_EMERGENCY_CONTACT_KEY = "emergency_contact";

export const BULK_EDIT_ENDPOINT = "v1/unit/bulk-update";

// GET endpoint for batch status polling ({endpoint}/{batch_id}).
export const BULK_EDIT_BATCH_STATUS_ENDPOINT = "v1/unit/batches";

/**
 * Ids array key in the bulk-update payload. Backend expects
 * { id_units: [...], filters: {...}, data: {...} } for manual selection.
 */
export const BULK_EDIT_IDS_KEY = "id_units";
